/**
 * Which client the admin's header picker shows, remembered per browser so a
 * reload stays on the client the admin was looking at instead of jumping back
 * to the first one.
 *
 * This is a convenience only, never a permission: the stored id just chooses
 * which of the clients the server already listed for this user to show. A
 * stored id that is not on that list is ignored, and every data request is
 * still scoped by the server.
 */

export const PICKED_CLIENT_KEY = 'agio-picked-client';

/** The slice of `localStorage` we use — lets tests pass a fake or a throwing one. */
export type ClientChoiceStorage = Pick<Storage, 'getItem' | 'setItem'>;

/** The remembered id, or null when there is none or storage is unavailable. */
export function readStoredClient(storage: ClientChoiceStorage): string | null {
  try {
    const v = storage.getItem(PICKED_CLIENT_KEY);
    return v ? v : null;
  } catch {
    return null; // private browsing / storage disabled — nothing remembered
  }
}

export function storeClient(storage: ClientChoiceStorage, clientId: string): void {
  try {
    storage.setItem(PICKED_CLIENT_KEY, clientId);
  } catch {
    // private browsing / storage disabled — the choice just won't survive a reload
  }
}

/**
 * The client the picker should show, given the clients the server listed:
 * the current pick if it is still listed, else the remembered one if it is
 * still listed, else the first client. Null only when there are no clients.
 */
export function resolvePickedClient(
  clientIds: readonly string[],
  picked: string | null,
  stored: string | null,
): string | null {
  if (picked && clientIds.includes(picked)) return picked;
  if (stored && clientIds.includes(stored)) return stored;
  return clientIds[0] ?? null;
}
